/**
 * The single param schema. Engine math, UI control rendering, URL codec,
 * clamping and live-vs-reinit routing all derive from this table —
 * adding a parameter is one SPECS row plus its use in the shaders.
 */
import { PALETTES, type PaletteId } from "./palettes";
import type { EmissionPattern, ParamKey, Params } from "./types";

export type Group = "Field" | "Strands" | "Motion" | "Appearance";

/**
 * live = present-pass uniform, applies to the finished ink instantly.
 * reinit = re-plots the piece from step 0 (nearly everything — the drawing
 * is a deterministic function of seed + params).
 */
export type UpdateClass = "live" | "reinit";

interface BaseSpec {
  key: ParamKey;
  label: string;
  group: Group;
  update: UpdateClass;
}

export interface RangeSpec extends BaseSpec {
  kind: "range";
  min: number;
  max: number;
  step: number;
  /** Optional display suffix, e.g. "°" or "px". */
  unit?: string;
  /** "log": the slider maps its track exponentially (min must be > 0). */
  scale?: "log";
}

export interface SelectSpec extends BaseSpec {
  kind: "select";
  options: ReadonlyArray<{ value: string; label: string }>;
}

export interface PaletteSpec extends BaseSpec {
  kind: "palette";
}

export type ParamSpec = RangeSpec | SelectSpec | PaletteSpec;

export const SPECS: readonly ParamSpec[] = [
  // Field
  { kind: "range", key: "flowScale", label: "Flow scale", group: "Field", update: "reinit", min: 0.2, max: 6, step: 0.01 },
  { kind: "range", key: "octaves", label: "Detail octaves", group: "Field", update: "reinit", min: 1, max: 4, step: 1 },
  { kind: "range", key: "roughness", label: "Roughness", group: "Field", update: "reinit", min: 0.2, max: 0.8, step: 0.01 },
  { kind: "range", key: "flowStrength", label: "Flow strength", group: "Field", update: "reinit", min: 0, max: 3, step: 0.01 },
  { kind: "range", key: "evolution", label: "Churn", group: "Field", update: "reinit", min: 0, max: 2, step: 0.01 },

  // Strands
  { kind: "range", key: "particleCount", label: "Strands", group: "Strands", update: "reinit", min: 1, max: 10000, step: 1, scale: "log" },
  { kind: "range", key: "lifetime", label: "Strand length", group: "Strands", update: "reinit", min: 2, max: 20, step: 0.1, unit: "s" },
  { kind: "range", key: "lifetimeJitter", label: "Length jitter", group: "Strands", update: "reinit", min: 0, max: 1, step: 0.01 },
  { kind: "range", key: "stagger", label: "Build-up", group: "Strands", update: "reinit", min: 0, max: 10, step: 0.1, unit: "s" },
  {
    kind: "select", key: "emission", label: "Emission", group: "Strands", update: "reinit",
    options: [
      { value: "plumes", label: "Plumes" },
      { value: "full", label: "Everywhere" },
      { value: "center", label: "Center" },
      { value: "ring", label: "Ring" },
      { value: "edges", label: "Edges" },
      { value: "line", label: "Line" },
    ],
  },
  { kind: "range", key: "plumeCount", label: "Plumes", group: "Strands", update: "reinit", min: 1, max: 4, step: 1 },
  { kind: "range", key: "spread", label: "Spread", group: "Strands", update: "reinit", min: 0.03, max: 0.4, step: 0.005 },
  { kind: "range", key: "halo", label: "Halo", group: "Strands", update: "reinit", min: 0, max: 1, step: 0.01 },

  // Motion
  { kind: "range", key: "speed", label: "Speed", group: "Motion", update: "reinit", min: 0, max: 3, step: 0.01 },
  { kind: "range", key: "windAngle", label: "Wind angle", group: "Motion", update: "reinit", min: 0, max: 360, step: 1, unit: "°" },
  { kind: "range", key: "windStrength", label: "Wind strength", group: "Motion", update: "reinit", min: 0, max: 1, step: 0.01 },
  { kind: "range", key: "vortex", label: "Vortex", group: "Motion", update: "reinit", min: -2, max: 2, step: 0.01 },
  { kind: "range", key: "attract", label: "Attract", group: "Motion", update: "reinit", min: -1, max: 1, step: 0.01 },

  // Appearance
  { kind: "range", key: "dotSize", label: "Dot size", group: "Appearance", update: "reinit", min: 0.5, max: 5, step: 0.05, unit: "px" },
  { kind: "range", key: "sizeJitter", label: "Size jitter", group: "Appearance", update: "reinit", min: 0, max: 1, step: 0.01 },
  { kind: "range", key: "dotOpacity", label: "Ink opacity", group: "Appearance", update: "reinit", min: 0.02, max: 1, step: 0.01 },
  { kind: "range", key: "glow", label: "Glow", group: "Appearance", update: "reinit", min: 0, max: 1, step: 0.01 },
  { kind: "range", key: "zoom", label: "Zoom", group: "Appearance", update: "reinit", min: 1, max: 3, step: 0.05, unit: "×" },
  { kind: "range", key: "exposure", label: "Exposure", group: "Appearance", update: "live", min: 0.5, max: 2, step: 0.01 },
  { kind: "palette", key: "palette", label: "Palette", group: "Appearance", update: "reinit" },
  { kind: "range", key: "backgroundShade", label: "Background", group: "Appearance", update: "reinit", min: 0, max: 0.15, step: 0.005 },
  { kind: "range", key: "symmetry", label: "Symmetry", group: "Appearance", update: "reinit", min: 1, max: 6, step: 1 },
] as const;

export const DEFAULTS: Readonly<Params> = Object.freeze({
  flowScale: 0.8,
  octaves: 2,
  roughness: 0.5,
  flowStrength: 1.0,
  evolution: 0.35,
  particleCount: 500,
  lifetime: 7,
  lifetimeJitter: 0.5,
  stagger: 3,
  emission: "plumes" as EmissionPattern,
  plumeCount: 1,
  spread: 0.1,
  halo: 0.15,
  speed: 0.7,
  windAngle: 0,
  windStrength: 0,
  vortex: 0,
  attract: 0,
  dotSize: 1.7,
  sizeJitter: 0.35,
  dotOpacity: 0.15,
  glow: 0.4,
  zoom: 1,
  exposure: 0.8,
  palette: "mono" as PaletteId,
  backgroundShade: 0.02,
  symmetry: 1,
});

export const SPEC_BY_KEY: ReadonlyMap<ParamKey, ParamSpec> = new Map(
  SPECS.map((s) => [s.key, s]),
);

export const REINIT_KEYS: ReadonlySet<ParamKey> = new Set(
  SPECS.filter((s) => s.update === "reinit").map((s) => s.key),
);

export const GROUPS: readonly Group[] = [
  "Strands",
  "Field",
  "Motion",
  "Appearance",
];

/**
 * Clamp/coerce an untrusted value (URL decode, presets) to a valid param
 * value, or return undefined if it cannot be salvaged.
 */
export function sanitizeParam<K extends ParamKey>(
  key: K,
  value: unknown,
): Params[K] | undefined {
  const spec = SPEC_BY_KEY.get(key);
  if (!spec) return undefined;
  if (spec.kind === "range") {
    const n = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(n)) return undefined;
    const clamped = Math.min(spec.max, Math.max(spec.min, n));
    const snapped = Math.round((clamped - spec.min) / spec.step) * spec.step + spec.min;
    // step-snapping can float-drift; round to the step's decimals
    const decimals = (spec.step.toString().split(".")[1] ?? "").length;
    return Number(
      Math.min(spec.max, snapped).toFixed(decimals),
    ) as Params[K];
  }
  if (spec.kind === "select") {
    const v = String(value);
    return spec.options.some((o) => o.value === v)
      ? (v as Params[K])
      : undefined;
  }
  // palette
  const v = String(value);
  return v in PALETTES ? (v as Params[K]) : undefined;
}
